/**
 * usePresence — wires the presence Redux slice to live WebSocket frames.
 *
 * Two server-pushed frames keep the slice in sync; the hook stays fully
 * socket-driven and never polls REST:
 *
 *   {type:"presence_snapshot", rows:[…]}  — one-shot, right after the
 *                                            "connected" frame; hydrates
 *                                            the whole branch in bulk.
 *   {type:"presence", user_id, status}    — incremental flips, broadcast
 *                                            tenant-wide on every
 *                                            connect/disconnect/away/DND/
 *                                            on_leave transition.
 *
 * Parents call `handlePresenceEvent(event)` from the chat WS dispatcher.
 * The hook also exposes `hydrate()` for components that need a manual
 * REST pull. Chat uses the optional interval only as a missed-frame safety
 * net; WebSocket events remain its primary update path.
 */
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import api from '../../api/api';
import hrmsApi from '../../hrms/api/api';
import { isWindowVisible, subscribeWindowVisibility } from '../../utils/windowVisibility';
import {
  bulkHydrate,
  setPresence,
  PresenceStatus,
} from '../../redux/slices/presence';

// REST fallback — retained for components outside the chat tree that don't
// have a WS subscription. The chat page itself uses the WS-pushed snapshot.
type PresenceRow = { user_id: string; status: PresenceStatus; leave_end_date?: string | null };
type AttendanceRow = {
  user_id?: string | number | null;
  employee_id?: string | number | null;
  clock_in_time?: string | null;
  clock_out_time?: string | null;
  out_time?: string | null;
};

const normalisePresenceRow = (row: any, today = todayInBrowser()): PresenceRow => {
  const leaveEndDate = row.leave_end_date ?? null;
  const hasExpiredLeave =
    row.status === 'on_leave' &&
    typeof leaveEndDate === 'string' &&
    leaveEndDate.slice(0, 10) < today;

  return {
    user_id: String(row.user_id),
    status: hasExpiredLeave ? 'offline' : (row.status || 'offline') as PresenceStatus,
    leave_end_date: hasExpiredLeave ? null : leaveEndDate,
  };
};

export const normalisePresenceSnapshot = (payload: any): PresenceRow[] => {
  const rows = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
  return rows
    .filter((r: any) => r && r.user_id)
    .map((row: any) => normalisePresenceRow(row));
};

const hasClockTime = (value: unknown): boolean =>
  typeof value === 'string' && value !== '' && value !== '00:00:00';

const todayInBrowser = (date = new Date()): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const openClockInUserIds = (payload: any): Set<string> => {
  const rows: AttendanceRow[] = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];
  const punches = new Map<string, { in: number; out: number }>();

  for (const row of rows) {
    const id = row?.user_id ?? row?.employee_id;
    if (id === null || id === undefined || id === '') continue;
    const current = punches.get(String(id)) || { in: 0, out: 0 };
    if (hasClockTime(row.clock_in_time)) current.in += 1;
    if (hasClockTime(row.clock_out_time ?? row.out_time)) current.out += 1;
    punches.set(String(id), current);
  }

  return new Set(
    [...punches.entries()]
      .filter(([, counts]) => counts.in > counts.out)
      .map(([id]) => id),
  );
};

const reconcileSnapshotWithClockRecords = (
  snapshot: PresenceRow[],
  clockedInUserIds: Set<string>,
): PresenceRow[] => {
  if (clockedInUserIds.size === 0) return snapshot;
  const seen = new Set<string>();
  const reconciled = snapshot.map((row) => {
    seen.add(row.user_id);
    // Leave and manually selected Away/DND remain higher-priority overlays.
    return clockedInUserIds.has(row.user_id) && row.status === 'offline'
      ? { ...row, status: 'online' as PresenceStatus }
      : row;
  });
  for (const user_id of clockedInUserIds) {
    if (!seen.has(user_id)) {
      reconciled.push({ user_id, status: 'online', leave_end_date: null });
    }
  }
  return reconciled;
};

const fetchPresenceSnapshot = async (): Promise<PresenceRow[]> => {
  try {
    // HRMS owns Clock In / Clock Out. Query it directly rather than relying
    // on a Messenger backend cache that may be stale after a rebuild.
    const { data } = await hrmsApi.get('/presence/snapshot');
    const snapshot = normalisePresenceSnapshot(data);
    // The snapshot service was rebuilt independently of the clock-in API and
    // can briefly (or incorrectly) report every user as offline. Attendance
    // records are the actual source of Clock In / Clock Out, so repair that
    // stale case from today's open punches.
    if (snapshot.some((row) => row.status === 'online')) return snapshot;
    const { data: attendance } = await hrmsApi.get('/attendanceAll', {
      params: { date: todayInBrowser() },
    });
    return reconcileSnapshotWithClockRecords(snapshot, openClockInUserIds(attendance));
  } catch {
    try {
      // Keep chat's merged leave/Away snapshot as a best-effort fallback when
      // HRMS is temporarily unavailable.
      const { data } = await api.get('/presence/snapshot');
      return normalisePresenceSnapshot(data);
    } catch {
      return [];
    }
  }
};

interface PresenceWsFrame {
  type: 'presence';
  user_id: string;
  status: PresenceStatus;
  leave_end_date?: string | null;
}

interface PresenceSnapshotFrame {
  type: 'presence_snapshot';
  rows: Array<{ user_id: string; status: PresenceStatus; leave_end_date?: string | null }>;
}

interface PresenceOptions {
  /** Receives each authoritative REST snapshot used to reconcile a screen's local state. */
  onHydrated?: (rows: PresenceRow[]) => void;
  /** Optional missed-frame safety net; WebSocket frames remain the primary update path. */
  refreshIntervalMs?: number;
}

export function usePresence({ onHydrated, refreshIntervalMs = 0 }: PresenceOptions = {}) {
  const dispatch = useDispatch();

  const hydrate = useCallback(async () => {
    const rows = await fetchPresenceSnapshot();
    // REST fallback may return a filtered subset — don't let it prune users.
    dispatch(bulkHydrate({ full: false, rows: rows.map((r) => ({ ...r, leave_end_date: r.leave_end_date ?? null })) }));
    return rows;
  }, [dispatch]);

  useEffect(() => {
    if (!onHydrated || !refreshIntervalMs) return;
    let active = true;
    const reconcile = () => {
      void hydrate()
        .then((rows) => {
          if (active) onHydrated(rows);
        })
        .catch(() => { /* the live WebSocket remains the primary path */ });
    };

    reconcile();
    // The safety net only matters for a screen somebody is looking at.
    const interval = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (!isWindowVisible()) return;
      reconcile();
    }, refreshIntervalMs);
    // ...but skipping ticks while hidden means the map can be arbitrarily old
    // by the time the window comes back, and nothing else repairs it: the
    // desktop socket stays OPEN in the tray (no reconnect, so no `connected`
    // frame) and the chat page's own focus handler refreshes conversations and
    // messages, not presence. So reconcile on the way back in.
    const offVisibility = subscribeWindowVisibility((visible) => {
      if (visible) reconcile();
    });
    return () => {
      active = false;
      window.clearInterval(interval);
      offVisibility();
    };
  }, [hydrate, onHydrated, refreshIntervalMs]);

  const handlePresenceEvent = useCallback(
    (event: any) => {
      if (!event) return;
      if (event.type === 'presence_snapshot') {
        const f = event as PresenceSnapshotFrame;
        if (!Array.isArray(f.rows)) return;
        dispatch(
          bulkHydrate({
            rows: f.rows
              .filter((r) => r && r.user_id && r.status)
              .map((row) => normalisePresenceRow(row)),
          }),
        );
        return;
      }
      if (event.type !== 'presence') return;
      const f = event as PresenceWsFrame;
      if (!f.user_id || !f.status) return;
      const row = normalisePresenceRow(f);
      dispatch(
        setPresence({
          userId: row.user_id,
          status: row.status,
          leaveEndDate: row.leave_end_date ?? null,
        }),
      );
    },
    [dispatch],
  );

  return { hydrate, handlePresenceEvent };
}

export default usePresence;
